import { useState, useMemo } from 'react';
import {
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Typography,
	List,
	ListItemButton,
	ListItemText,
	Button,
	Box,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import NewDatalakeModal from './NewDatalakeModal';
import logoMapping from '../../assets/images/services-logos';
import { Link, useParams } from 'react-router';
import SubSidebar from '../../components/SubSidebar';
import JobStatus from './JobStatus';

const DatalakeSidebar = ({
	connections,
	services,
	jobDataPromise,
	tenant,
	user,
}) => {
	const [open, setOpen] = useState(false);
	const { connectionId } = useParams(); // get connection ID if in route

	const groupedConnections = connections.reduce((acc, connection) => {
		const serviceInfo = services.find(
			(service) => service.id == connection.service_id
		);
		const serviceName = serviceInfo ? serviceInfo.name : 'Unknown Service';
		if (!acc[serviceName]) {
			acc[serviceName] = {
				connections: [],
				logo: serviceInfo ? logoMapping[serviceInfo.logo] : undefined,
				defaultExpanded: false,
			};
		}
		acc[serviceName].connections.push(connection);
		return acc;
	}, {});

	const servicesAndConnections = useMemo(() => {
		return Object.entries(groupedConnections).map(
			([serviceName, { connections, logo }], index) => (
				<Accordion
					key={serviceName}
					defaultExpanded={connections.some((c) => c.id == connectionId)}
				>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls={`panel${index}a-content`}
						id={`panel${index}a-header`}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							{logo && (
								<img
									src={logo}
									alt={`${serviceName} logo`}
									style={{ width: 24, height: 24 }}
								/>
							)}
							<Typography>{serviceName}</Typography>
						</Box>
					</AccordionSummary>
					<AccordionDetails>
						<List component='div' disablePadding>
							{connections.map((connection) => (
								<ListItemButton
									selected={connection.id == connectionId}
									key={connection.id}
									component={Link}
									to={`/tenants/${tenant.id}/datalake/${connection.id}`}
								>
									<ListItemText>
										<span>{connection.name}</span>
										{connection.environment && (
											<span style={{ textTransform: 'capitalize' }}>
												{` (${connection.environment}) `}
											</span>
										)}
									</ListItemText>
								</ListItemButton>
							))}
						</List>
					</AccordionDetails>
				</Accordion>
			)
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connectionId]);

	// Handle modal open/close
	const handleModalOpen = () => setOpen(true);
	const handleModalClose = () => setOpen(false);

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
			<Typography variant='h4'>Datalake Connections</Typography>
			<SubSidebar tenant={tenant} activeTab={'Connections'} user={user}>
				{(user.role == 'altvia_admin' || user.role == 'altvia_power_user') && (
					<Button
						startIcon={<AddCircleOutlineIcon />}
						onClick={handleModalOpen}
						variant='outlined'
						fullWidth
						sx={{ mb: 2 }}
					>
						Create New Datalake Connection
					</Button>
				)}
				{servicesAndConnections}
				<JobStatus
					tenant={tenant}
					user={user}
					jobDataPromise={jobDataPromise}
				/>
			</SubSidebar>
			<NewDatalakeModal
				open={open}
				handleClose={handleModalClose}
				services={services}
				connections={connections}
			/>
		</Box>
	);
};

export default DatalakeSidebar;
